/**
 * Worker-safe patch application (Collab Phase 3).
 *
 * Duplicates the apply semantics of `core/history.js` (`applyPatches`) and
 * `core/state.js` (`getByPath`/`setByPath`) for the Worker bundle. NEVER
 * import core/history.js into the worker — its import chain pulls browser
 * modules (state.js → asset-store/idb/…) and a circular init. Parity between
 * this file and history.js is pinned by vectors in collab-protocol-tests.js.
 *
 * One deliberate strengthening over the core versions: paths containing
 * `__proto__` / `constructor` / `prototype` segments THROW here instead of
 * silently traversing — the server applies patches from other clients, and
 * setByPath materializes intermediate objects along the path (prototype
 * pollution guard; validators reject these paths earlier, this is defense in
 * depth). The core client versions only ever apply locally-authored patches.
 */

import { FORBIDDEN_PATH_SEGMENTS, isEditableIdRepairAbsentPatch } from './collab-protocol.js';

function splitPath(path) {
  const keys = String(path).split('.');
  for (const key of keys) {
    if (FORBIDDEN_PATH_SEGMENTS.includes(key)) {
      throw new Error(`Forbidden path segment in "${path}"`);
    }
  }
  return keys;
}

/** Parity with state.js getByPath (plus the forbidden-segment throw). */
export function getByPath(obj, path) {
  const keys = splitPath(path);
  let current = obj;
  for (const key of keys) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

/** Parity with state.js setByPath (creates intermediate objects). */
export function setByPath(obj, path, value) {
  const keys = splitPath(path);
  let current = obj;
  for (let i = 0; i < keys.length - 1; i += 1) {
    if (current[keys[i]] == null) current[keys[i]] = {};
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
}

/** Parity with history.js applyPatches — byte-identical application order. */
export function applyPatches(state, patches) {
  for (const patch of patches) {
    switch (patch.op) {
      case 'replace':
        if (patch.absent === true) {
          if (!isEditableIdRepairAbsentPatch(patch))
            throw new Error('Invalid repair absence patch');
          const keys = splitPath(patch.path);
          const field = keys.pop();
          const parent = getByPath(state, keys.join('.'));
          if (!parent || typeof parent !== 'object')
            throw new Error('Missing repair absence parent');
          delete parent[field];
        } else setByPath(state, patch.path, patch.value);
        break;
      case 'insert': {
        const arr = getByPath(state, patch.path);
        arr.splice(patch.index, 0, structuredClone(patch.item));
        break;
      }
      case 'remove': {
        const arr = getByPath(state, patch.path);
        arr.splice(patch.index, 1);
        break;
      }
      case 'move': {
        const arr = getByPath(state, patch.path);
        const [item] = arr.splice(patch.from, 1);
        arr.splice(patch.to, 0, item);
        break;
      }
      default:
        console.warn(`[patch-utils] Unknown patch op: ${patch.op}`);
    }
  }
}
